import { useEffect, useMemo } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { ArrowLeft, CalendarX, MapPin, Printer, Send } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useHasJobsFeature } from '@/hooks/use-has-feature'
import { useEvent } from '@/stores/events.store'
import { useBooking } from '@/stores/bookings.store'
import { getJob, listEventAddons, listJobEvents } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEventAddonsRequestSchema } from '@/gen/cortado/v1/event_plan_pb'
import { GetJobRequestSchema, ListJobEventsRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import { formatDateTimeRange } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { ActionGate } from '@/components/shared/action-gate'
import { AssignStaffSheet } from '@/components/events/assign-staff-sheet'
import { BookingQuestionnaireEditorSheet } from '@/components/events/id/booking-questionnaire-editor-sheet'
import { CalendarDescriptionCard } from '@/components/events/id/calendar-description-card'
import { DirectionsBody } from '@/components/events/id/directions-body'
import { EventStageDropdown } from '@/components/events/id/event-stage-dropdown'
import { PackingBody } from '@/components/events/id/packing-body'
import { EventFactsCard } from '@/components/pipeline/event-facts-card'
import { EventReadinessStrip, EVENT_SECTION_IDS } from '@/components/pipeline/event-readiness-strip'
import { JobEventSwitcher } from '@/components/pipeline/job-event-switcher'
import { JobRail } from '@/components/pipeline/job-rail'
import {
  jobDetailEventTitle,
  jobEarliestEventDay,
} from '@/components/pipeline/job-detail-view'
import {
  LOGISTICS_CHECK_ORDER,
  logisticsChecklist,
  logisticsReadyCount,
  type LogisticsCheckKey,
} from '@/components/pipeline/job-logistics'
import { LogisticsSheet, type LogisticsSheetCheck } from '@/components/pipeline/logistics-sheet'
import { jobChildBreadcrumbs } from '@/components/pipeline/pipeline-breadcrumbs'
import {
  AddonsSection,
  OnSiteSection,
  RunSheetSection,
  StaffingSection,
  TimelineSection,
} from '@/components/pipeline/run-sheet-sections'

/** The add-ons section has no readiness check, so it anchors on its own id. */
const ADDONS_SECTION_ID = 'event-addons'

// `$id_` opts out of nesting under the job page route: this is its own page,
// not an outlet inside the job detail.
export const Route = createFileRoute('/_app/_home/pipeline/$id_/events/$eventId')({
  component: PipelineEventRouteFromParams,
  // The logistics step open in a sheet over the run sheet, the same keys the job page uses.
  validateSearch: z.object({
    logistics: z
      .enum(LOGISTICS_CHECK_ORDER as [LogisticsCheckKey, ...LogisticsCheckKey[]])
      .optional()
      .catch(undefined),
  }),
})

function PipelineEventRouteFromParams() {
  const { id, eventId } = Route.useParams()
  const { logistics } = Route.useSearch()
  return <PipelineEventRoute jobId={id} eventId={eventId} openLogistics={logistics} />
}

interface PipelineEventRouteProps {
  jobId: string
  eventId: string
  /** The sheet open over the page, from the URL. */
  openLogistics?: LogisticsCheckKey
}

export function PipelineEventRoute({ jobId, eventId, openLogistics }: PipelineEventRouteProps) {
  const navigate = useNavigate()
  const { hasFeature, hasLoaded } = useHasJobsFeature()

  useEffect(() => {
    // Without the Pipeline, the event still has its standalone page.
    if (hasLoaded && !hasFeature) navigate({ to: '/events/$id', params: { id: eventId }, replace: true })
  }, [eventId, hasFeature, hasLoaded, navigate])

  if (!hasLoaded || !hasFeature) {
    return <div className="flex min-h-[50dvh] items-center justify-center">Loading...</div>
  }

  return <PipelineEventPage jobId={jobId} eventId={eventId} openLogistics={openLogistics} />
}

function NotFound({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-1 flex-col rounded-lg border border-dashed py-6 md:py-0">
        <Empty>
          <EmptyMedia variant="icon">
            <CalendarX className="h-6 w-6" />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </Empty>
      </div>
    </div>
  )
}

function isLogisticsSheetCheck(key: LogisticsCheckKey): key is LogisticsSheetCheck {
  return key === 'timeline' || key === 'inventory' || key === 'directions' || key === 'calendar'
}

/**
 * One booked event of a pipeline job as its run sheet: the facts card, then
 * the numbered document (01 Staffing, 02 Add-ons, 03 Timeline, 04 Inventory,
 * 05 On-site details) with the calendar description, directions and the
 * event's tasks in the rail. The page reads; each section's action opens the
 * same sheet the job page's Logistics card opens, keyed in the URL as
 * `?logistics=`. Packing stays live on the page because marking a row packed
 * is a status toggle, not a list edit. "Run sheet" opens the printable run of
 * show. Multi-event bookings get a tab row to move between events without
 * going back to the job.
 */
export function PipelineEventPage({ jobId, eventId, openLogistics }: PipelineEventRouteProps) {
  const navigate = useNavigate()

  const jobRequest = useMemo(() => create(GetJobRequestSchema, { jobId }), [jobId])
  const eventsRequest = useMemo(() => create(ListJobEventsRequestSchema, { jobId }), [jobId])
  const addonsRequest = useMemo(() => create(ListEventAddonsRequestSchema, { eventId }), [eventId])
  const jobQuery = useQuery(getJob, jobRequest, { enabled: Boolean(jobId) })
  const eventsQuery = useQuery(listJobEvents, eventsRequest, { enabled: Boolean(jobId) })
  const addonsQuery = useQuery(listEventAddons, addonsRequest, { enabled: Boolean(eventId) })
  const { event, isLoading: isEventLoading, isError: isEventError } = useEvent(eventId)
  const { booking } = useBooking(event?.bookingId)

  const job = jobQuery.data?.job
  const events = eventsQuery.data?.events ?? []
  const lite = event ? events.find((row) => row.id === event.id) : undefined
  const heading = event ? (lite ? jobDetailEventTitle(lite) : event.eventTypeKey || 'Event') : ''
  const breadcrumbs = useMemo(() => (job && event ? jobChildBreadcrumbs(job, heading) : undefined), [job, event, heading])
  useBreadcrumbFromRoute(breadcrumbs)

  if (jobQuery.isLoading || isEventLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          <Skeleton className="h-96 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (jobQuery.isError || !job) {
    return <NotFound title="Job not found" description="We couldn't load this job. It may have been removed." />
  }
  // An event id from another booking (a stale or hand-edited URL) is not this job's.
  if (isEventError || !event || !job.bookingId || event.bookingId !== job.bookingId) {
    return <NotFound title="Event not found" description="This event isn't part of this job's booking." />
  }

  const meta = [
    formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone) || 'Date TBD',
    event.eventGuests ? `${event.eventGuests} ${event.eventGuests === 1 ? 'guest' : 'guests'}` : '',
    event.eventLocation ?? '',
  ]
    .filter(Boolean)
    .join(' · ')
  const readiness = lite?.readiness ? logisticsReadyCount(logisticsChecklist(lite)) : undefined
  const hasLocation = !!event.eventLocation?.trim()

  const openSheet = (key: LogisticsCheckKey | undefined) =>
    navigate({
      to: '/pipeline/$id/events/$eventId',
      params: { id: jobId, eventId },
      search: { logistics: key },
      replace: true,
    })
  const closeSheet = (open: boolean) => {
    if (!open) openSheet(undefined)
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
            {event.archivedAt && <Badge variant="secondary">Archived</Badge>}
          </div>
          <div className="mt-1.5 text-xs/normal text-muted-foreground">{meta}</div>
          <Button asChild size="sm" variant="outline" className="mt-2">
            <Link to="/pipeline/$id" params={{ id: job.id }}>
              <ArrowLeft data-icon="inline-start" />
              Back to {formatFullName(job.contactFirstName, job.contactLastName, '') || job.title || 'job'}
            </Link>
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {readiness && (
            <span className="text-xs text-muted-foreground">
              {readiness.ready} of {readiness.total} ready
            </span>
          )}
          <Button asChild size="sm" variant="outline">
            <Link to="/pipeline/$id/events/$eventId/run-sheet" params={{ id: job.id, eventId: event.id }}>
              <Printer data-icon="inline-start" />
              Run sheet
            </Link>
          </Button>
          {/* Placeholder until Notify team has a send and a record of who was told. */}
          <Button size="sm" variant="brand" disabled title="Notify team is coming soon">
            <Send data-icon="inline-start" />
            Notify team
          </Button>
          <EventStageDropdown eventId={event.id} currentStage={event.stage} bookingId={event.bookingId} />
        </div>
      </div>

      <JobEventSwitcher jobId={job.id} events={events} currentEventId={event.id} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <EventFactsCard event={event} />

          <div data-slot="card" className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b bg-muted/30 px-5 py-4">
              <h2 className="text-base font-semibold">Run sheet</h2>
              {lite && <EventReadinessStrip event={lite} />}
            </div>
            <div className="divide-y px-5">
              <StaffingSection
                id={EVENT_SECTION_IDS.staff}
                event={event}
                staff={eventsQuery.isLoading ? undefined : (lite?.staff ?? [])}
                onAssign={() => openSheet('staff')}
              />
              <AddonsSection
                id={ADDONS_SECTION_ID}
                addons={addonsQuery.isLoading ? undefined : (addonsQuery.data?.addons ?? [])}
                isError={addonsQuery.isError}
              />
              <TimelineSection id={EVENT_SECTION_IDS.timeline} event={event} onEdit={() => openSheet('timeline')} />
              <RunSheetSection id={EVENT_SECTION_IDS.inventory} number="04" title="Inventory" summary="Tap a row to mark it packed">
                <PackingBody event={event} />
              </RunSheetSection>
              <OnSiteSection
                id={EVENT_SECTION_IDS.questionnaire}
                event={event}
                booking={booking}
                onEdit={() => openSheet('questionnaire')}
              />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <section id={EVENT_SECTION_IDS.calendar} className="scroll-mt-4 empty:hidden">
            <CalendarDescriptionCard event={event} />
          </section>
          <section id={EVENT_SECTION_IDS.directions} className="scroll-mt-4">
            <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-base font-semibold">
                  <MapPin className="size-4 text-muted-foreground" aria-hidden="true" />
                  Directions
                </h2>
                {!hasLocation && (
                  <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="edit" noun="events">
                    <Button type="button" size="sm" variant="outline" onClick={() => openSheet('directions')}>
                      Set location
                    </Button>
                  </ActionGate>
                )}
              </div>
              <DirectionsBody event={event} className="min-h-56" />
            </div>
          </section>
          <JobRail job={job} stage="event" eventId={event.id} eventDay={jobEarliestEventDay(lite ? [lite] : events)} />
        </div>
      </div>

      <AssignStaffSheet
        eventId={event.id}
        eventTimezone={event.eventTimezone}
        open={openLogistics === 'staff'}
        onOpenChange={closeSheet}
      />
      {lite && (
        <LogisticsSheet
          jobId={job.id}
          event={lite}
          check={openLogistics && isLogisticsSheetCheck(openLogistics) ? openLogistics : undefined}
          onOpenChange={closeSheet}
        />
      )}
      {booking && (
        <BookingQuestionnaireEditorSheet
          layout="pipeline"
          organizationId={booking.organizationId}
          bookingId={booking.id}
          eventTypeKey={booking.eventTypeKey}
          questionnaire={booking.questionnaire}
          open={openLogistics === 'questionnaire'}
          onOpenChange={closeSheet}
        />
      )}
    </div>
  )
}
